import { useQuery, useQueryClient, UseQueryOptions } from 'react-query'

import { notificationsApi } from './notifications.api'
import {
  COUNT_NEW_NOTIFICATIONS_KEY,
  NOTIFICATIONS_KEY,
  NOTIFICATIONS_SETTINGS_KEY
} from './notifications.constants'
import { NotificationSettingsCategories } from './notifications.types'
import { components } from '../../../generated'
import { useRouter } from 'next/router'
import { getToken } from '../../../utils'

const INTERVAL_REQUEST_COUNT_NEW_NOTIFICATIONS = 1000 * 60

export const useQueryCountNewNotifications = <
  T = components['schemas']['AlertsCountResource']
>(
  options?: UseQueryOptions<
    components['schemas']['AlertsCountResource'],
    unknown,
    T
  >
) => {
  const token = getToken()
  const { locale } = useRouter()
  const data = useQuery(
    [COUNT_NEW_NOTIFICATIONS_KEY, locale],
    () => notificationsApi.getCountNewNotification(locale),
    {
      refetchInterval: INTERVAL_REQUEST_COUNT_NEW_NOTIFICATIONS,
      ...options,
      enabled: !!token
    }
  )

  return data
}

export const useQueryNotifications = <
  T = components['schemas']['AlertResource'][]
>(
  type: components['schemas']['AlertServiceType'],
  options?: UseQueryOptions<
    components['schemas']['AlertResource'][],
    unknown,
    T
  >
) => {
  const queryClient = useQueryClient()
  const { locale } = useRouter()

  const data = useQuery(
    [NOTIFICATIONS_KEY, type, locale],
    () => notificationsApi.getNotifications(type, locale),
    {
      staleTime: 0,
      ...options,
      onSuccess: (data) => {
        queryClient.setQueryData<components['schemas']['AlertsCountResource']>(
          COUNT_NEW_NOTIFICATIONS_KEY,
          (countNotification) => ({
            ...countNotification,
            count: {
              ...countNotification?.count,
              [type]: Array.isArray(data)
                ? (data ?? []).filter((notification) => !notification?.isRead)
                    .length
                : countNotification?.count?.[type]
            }
          })
        )
        options?.onSuccess?.(data)
      }
    }
  )

  return data
}

export const useQueryNotificationsSettings = <
  T = NotificationSettingsCategories
>(
  type: components['schemas']['AlertServiceType'],
  options?: UseQueryOptions<NotificationSettingsCategories, unknown, T>
) => {
  const { locale } = useRouter()
  const data = useQuery(
    [NOTIFICATIONS_SETTINGS_KEY, type, locale],
    () => notificationsApi.getNotificationsSettings(type, locale),
    options
  )

  return data
}
